import { useMemo } from "react";
import { ServiceInfoDc, TableInfoDc } from "@evergis/api";

import { getLayerInfo } from "../../../../../../evergis/core/layer/getLayerInfo";
import { isReadable } from "../../../../../../evergis/core/permissions";
import { extractResourceName } from "../../../../../../utils/resourceName";
import { useDefaultScaleRange } from "../../../../../../hooks/style/useDefaultScaleRange";
import { LayerInfo } from "../../../../../../interfaces/resourceInfo";

export type LayerStructureInfo = ServiceInfoDc & {
  isFilterable: boolean;
};

export type TableStructureInfo = TableInfoDc & {
  layers: LayerStructureInfo[];
};

const useGetTableStructure = (tables: TableInfoDc[], layers: ServiceInfoDc[]): TableStructureInfo[] => {
  const defaultScaleRange = useDefaultScaleRange();

  return useMemo(() => {
    const filteredTables: TableInfoDc[] = (tables && tables.filter(table => isReadable(table.permissions))) || [];

    return filteredTables.map((table: TableInfoDc): TableStructureInfo => {
      const filteredLayers = Array.isArray(layers)
        ? layers.reduce<LayerInfo[]>((acc, layer): LayerInfo[] => {
            const layerInfo = getLayerInfo(layer, defaultScaleRange);

            if (
              !isReadable(layer.permissions) ||
              extractResourceName(layerInfo.tableName) !== extractResourceName(table.name)
            )
              return acc;

            return [...acc, layerInfo];
          }, [])
        : [];

      return {
        ...table,
        layers: filteredLayers.map((layer: LayerInfo) => {
          const { condition: layerCondition } = layer;

          return {
            ...layer,
            isFilterable: filteredLayers.length === 1 && !!layerCondition,
          };
        }) as LayerStructureInfo[],
      };
    });
  }, [tables, layers, defaultScaleRange]);
};

export { useGetTableStructure };
